import type { Screen, ScreenId } from "./ScreenManager";
import { iconExit, iconSettings, iconRobot, iconPlayers } from "./assets";
import { asset } from "../../platform/assets";
import { audio, loadAudioPrefs, saveAudioPrefs, type AudioPrefs } from "./sound";

export type HomeAction = "computer" | "local" | "settings" | "exit";

export class HomeScreen implements Screen {
  readonly id: ScreenId = "home";

  private prefs: AudioPrefs;

  constructor(private onAction: (action: HomeAction) => void) {
    this.prefs = loadAudioPrefs();
  }

  render(): HTMLElement {
    const el = document.createElement("div");
    el.innerHTML = `
      <div class="home__bg" aria-hidden="true"></div>
      <div class="home__topbar">
        <button class="icon-btn js-exit" aria-label="Exit game">${iconExit}</button>
        <div class="home__topbar-group">
          <button class="icon-btn js-settings" aria-label="Settings">${iconSettings}</button>
        </div>
      </div>
      <nav class="home" aria-label="Game modes">
        <button class="mode-card mode-card--computer js-computer">
          <span class="mode-card__art">${iconRobot}</span>
          <span class="mode-card__label">Computer</span>
        </button>
        <button class="mode-card mode-card--local js-local">
          <span class="mode-card__art">${iconPlayers}</span>
          <span class="mode-card__label">Local</span>
        </button>
      </nav>

      <div class="overlay settings-overlay" hidden>
        <section class="panel settings-panel" aria-label="Settings">
          <h2 class="settings-panel__title">Settings</h2>
          <div class="set-row">
            <span class="set-row__label">Music</span>
            <input class="set-slider" type="range" min="0" max="100" step="1"
                   data-chan="music" aria-label="Music volume">
            <button class="switch" data-chan="music" type="button"
                    role="switch" aria-label="Music on/off">
              <span class="switch__knob"></span>
            </button>
          </div>

          <div class="set-row">
            <span class="set-row__label">Sound FX</span>
            <input class="set-slider" type="range" min="0" max="100" step="1"
                   data-chan="sfx" aria-label="Sound effects volume">
            <button class="switch" data-chan="sfx" type="button"
                    role="switch" aria-label="Sound effects on/off">
              <span class="switch__knob"></span>
            </button>
          </div>

          <button class="btn btn--green settings-panel__done js-settings-close">Done</button>
        </section>
      </div>

      <div class="overlay confirm-overlay exit-confirm" hidden>
        <section class="panel settings-panel" aria-label="Confirm exit">
          <h2 class="settings-panel__title">Exit game?</h2>
          <p class="confirm-overlay__msg">Are you sure you want to leave Ludo Legacy?</p>
          <button class="btn btn--green js-exit-yes">Yes, exit</button>
          <button class="btn btn--gold js-exit-no">Stay</button>
        </section>
      </div>
    `;

    // Inline bg with a relative URL so the Y8 zip/iframe (sub-path) loads
    // the same file the Play Store build gets from the absolute CSS URL.
    const bg = el.querySelector<HTMLElement>(".home__bg");
    if (bg) {
      try {
        bg.style.backgroundImage = `url("${asset("bg/home-menu.jpg")}")`;
      } catch {
        /* CSS fallback stays */
      }
    }

    const bind = (selector: string, action: HomeAction) =>
      el.querySelector(selector)?.addEventListener("click", () => this.onAction(action));

    bind(".js-computer", "computer");
    bind(".js-local", "local");

    // Exit asks first, then lets the app decide how to close (see index.ts).
    const exitConfirm = el.querySelector<HTMLElement>(".exit-confirm")!;
    el.querySelector(".js-exit")!.addEventListener("click", () => {
      exitConfirm.hidden = false;
    });
    el.querySelector(".js-exit-yes")!.addEventListener("click", () => {
      exitConfirm.hidden = true;
      this.onAction("exit");
    });
    el.querySelector(".js-exit-no")!.addEventListener("click", () => {
      exitConfirm.hidden = true;
      audio.play("back");
    });

    // Settings panel
    const overlay = el.querySelector<HTMLElement>(".settings-overlay")!;
    el.querySelector(".js-settings")!.addEventListener("click", () => {
      overlay.hidden = false;
    });
    el.querySelector(".js-settings-close")!.addEventListener("click", () => {
      overlay.hidden = true;
    });

    // Sliders (volume) + switches (on/off), kept in sync and persisted.
    overlay.querySelectorAll<HTMLInputElement>(".set-slider").forEach((slider) => {
      const chan = slider.dataset.chan as "music" | "sfx";
      slider.value = String(Math.round((chan === "music" ? this.prefs.musicVol : this.prefs.sfxVol) * 100));
      const paintFill = (): void =>
        slider.style.setProperty("--fill", `${slider.value}%`);
      paintFill();
      slider.addEventListener("input", () => {
        const vol = Number(slider.value) / 100;
        if (chan === "music") {
          this.prefs.musicVol = vol;
          audio.setMusicVolume(vol);
        } else {
          this.prefs.sfxVol = vol;
          audio.setSfxVolume(vol);
        }
        paintFill();
        this.persist();
      });
      // Turning a channel's volume up from zero re-enables it.
      slider.addEventListener("change", () => {
        if (Number(slider.value) > 0) return;
        this.setChannel(chan, false);
        this.syncSwitches(overlay);
      });
    });

    overlay.querySelectorAll<HTMLButtonElement>(".switch").forEach((sw) => {
      sw.addEventListener("click", () => {
        const chan = sw.dataset.chan as "music" | "sfx";
        const on = sw.getAttribute("aria-pressed") !== "true";
        this.setChannel(chan, on);
        if (on && chan === "sfx") audio.play("confirm"); // audible feedback
        this.syncSwitches(overlay);
      });
    });

    this.syncSwitches(overlay);
    return el;
  }

  private setChannel(chan: "music" | "sfx", on: boolean): void {
    if (chan === "music") {
      this.prefs.musicOn = on;
      audio.setMusicEnabled(on);
    } else {
      this.prefs.sfxOn = on;
      audio.setSoundEnabled(on);
    }
    this.persist();
  }

  private syncSwitches(overlay: HTMLElement): void {
    overlay.querySelectorAll<HTMLButtonElement>(".switch").forEach((sw) => {
      const chan = sw.dataset.chan as "music" | "sfx";
      sw.setAttribute("aria-pressed", String(chan === "music" ? this.prefs.musicOn : this.prefs.sfxOn));
    });
  }

  private persist(): void {
    saveAudioPrefs(this.prefs);
  }
}
